import React, { useState, useEffect } from 'react';
import { Banco } from '../../types/financeiro';
import { financeiroService } from '../../services/financeiroService';
import { X, Building2, Save, Loader2, AlertCircle } from 'lucide-react';

interface BancoFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  banco: Banco | null;
  onSuccess: () => void;
}

export const BancoFormModal: React.FC<BancoFormModalProps> = ({
  isOpen,
  onClose,
  banco,
  onSuccess,
}) => {
  const [nome, setNome] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNome(banco?.nome || '');
      setError(null);
    }
  }, [isOpen, banco]);

  if (!isOpen) return null;

  const isEditing = Boolean(banco && banco.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setError('Por favor, informe o nome da instituição financeira.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing && banco) {
        const res = await financeiroService.updateBanco(banco.id, nome);
        if (!res.success) {
          setError(res.error || 'Erro ao atualizar instituição bancária.');
          setLoading(false);
          return;
        }
      } else {
        const res = await financeiroService.createBanco(nome);
        if (!res.success) {
          setError(res.error || 'Erro ao cadastrar instituição bancária.');
          setLoading(false);
          return;
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar banco:', err);
      setError(err.message || 'Erro inesperado ao salvar banco.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-base">
                {isEditing ? 'Editar Instituição Bancária' : 'Nova Instituição Bancária'}
              </h3>
              <p className="text-xs text-slate-500">
                {isEditing ? `Alterando dados do banco #${banco?.id}` : 'Cadastre um novo banco para movimentações financeiras'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Nome da Instituição Financeira <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Itaú Unibanco, Bradesco, Santander, Banco do Brasil..."
                required
                autoFocus
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-slate-800 placeholder-slate-400 font-medium"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">
                Nome de exibição nas opções de contas a pagar, receber e boletos.
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-xs hover:shadow-md transition-all disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Atualizar Banco' : 'Cadastrar Banco'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
